import { Check, DoneAll, Link } from "@mui/icons-material";
import { Box, Stack, Typography, Chip} from "@mui/material";
import { SUCCESS_PRIMARY_MUI } from "../../../enums/Colors";
import usePrimaryColor from "../../../hooks/usePrimaryColor";
import useUser from "../../../hooks/useUser";
import { useIsMobile } from "../../../utils/Functions";
import MessageMenu from "./MessageMenu";
import { Fragment, memo, useMemo } from "react";
import { styled } from '@mui/material/styles';
import { useTranslation } from "react-i18next";
import { APPOINTEMENT_DETAILS } from "../../../enums/UrlPaths";

export default memo(function Message({ msg, idx, update }) {
  const { profile } = useUser();
  const isMobile = useIsMobile();
  const loggedinUserId = profile?.id;
  const textcolor = "white";
  const color = usePrimaryColor();
  const read = Boolean(msg?.read || update);

  const isSender = loggedinUserId !== Number(msg.receiverId);

  const getDynamicMaxWidth = (text) => {
    const length = text.length;
    if (isMobile) return "65vw";
    if (length < 30) return "15vw";
    if (length < 60) return "25vw";
    if (length < 100) return "45vw";
    return "60vw";
  };

  return (
    <Stack mb={3}>
      <Box
        key={idx}
        sx={(theme) => ({
          position: "relative",
          maxWidth: getDynamicMaxWidth(msg?.body),
          alignSelf: isSender ? "flex-end" : "flex-start",
          backgroundColor: isSender ? color : SUCCESS_PRIMARY_MUI,
          color: isSender ? textcolor : theme.palette.text.primary,
          borderRadius: 8,
          borderBottomLeftRadius: isSender ? "default" : 0,
          borderBottomRightRadius: isSender ? 0 : "default",
          p: 2,
          wordBreak: "break-word",
          ml: isSender ? "auto" : 0,
          boxShadow: 0,
        })}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.95rem",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontWeight: 400,
              letterSpacing: "0.2px",
              color: textcolor,
              flex: 1,
            }}
            data-testid={`inbox.chat.message.${msg.id}`}
          >
            <MemoizedLinkifyText text={msg.body}/>
          </Typography>

          {isSender && (
            <Box sx={{ alignSelf: "flex-end", display: "inline-flex" }}>
              <MessageTick read={read} />
            </Box>
          )}
        </Box>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent={isSender ? "flex-end" : "flex-start"}
      >
        <Typography
          variant="caption"
          sx={(theme) => ({
            display: "block",
            textAlign: isSender ? "right" : "left",
            fontSize: "0.7rem",
            mt: 1,
          })}
        >
          {String(msg?.sentAt).slice(0, 17)}
        </Typography>
        {isSender ? <MessageMenu messageId={msg?.id} /> : null}
      </Stack>
    </Stack>
  );
});

function MessageTick({ read = false }) {
  return read ? (
    <DoneAll sx={{ color: "white" }} fontSize="small" />
  ) : (
    <Check sx={{ color: "white" }} fontSize="small" />
  );
}

export const LinkChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.primary.light,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  height: 'auto',
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    padding: '4px 8px',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color:"white", 
  },
}));

const appointmentIdRegex = /%appointment_id\/(\d+)%/g;
const urlRegex = /(https?:\/\/[^\s]+)/g;
const combinedRegex = new RegExp(`${urlRegex.source}|${appointmentIdRegex.source}`, 'g');

const MemoizedLinkifyText = memo(function LinkifyText({ text }) {
  const { t } = useTranslation();

  return useMemo(() => parseLinkifyText(text, t), [text, t]);
});

function parseLinkifyText(text, t) {
  const result = [];
  const regex = new RegExp(combinedRegex.source, 'g');

  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(<Fragment key={lastIndex}>{text.slice(lastIndex, match.index)}</Fragment>);
    }

    if (match[0].startsWith('http')) {
      const url = match[0];
      result.push(
        <LinkChip
          key={match.index}
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          label={url.replace(/^https?:\/\//, '').split('/')[0]}
          icon={<Link fontSize="small" />}
          clickable
          sx={{
            m: 0.5,
            '&:first-of-type': { ml: 0 },
            '&:last-of-type': { mr: 0 },
          }}
        />
      );
    } else if (match[0].startsWith('%appointment_id')) {
      const appointmentId = match[2]; 
      const link = `${APPOINTEMENT_DETAILS}${appointmentId}`;
      result.push(
        <LinkChip
          key={match.index}
          component="a"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          label={t("Appointment")} 
          icon={<Link fontSize="small" />}
          clickable
          sx={{
            m: 0.5,
            '&:first-of-type': { ml: 0 },
            '&:last-of-type': { mr: 0 },
          }}
        />
      );
    }

    lastIndex = regex.lastIndex;
  }

  // Push remaining text after the last match
  if (lastIndex < text.length) {
    result.push(<Fragment key={lastIndex}>{text.slice(lastIndex)}</Fragment>);
  }

  return result;
}
